/**
 * Railway 백엔드 API 통신 모듈 v3
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 🔥 P0 수정:
 * - 절대주소 사용 (https://api.sajuos.com)
 * - /view/{job_id}?token={token} 형식
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

import type {
  CalculateRequest,
  CalculateResponse,
  InterpretRequest,
  InterpretResponse,
  HourOption,
  ConcernOption,
} from '@/types';

// ============ 🔥 환경변수 - 절대주소 강제 ============

// 🔥 P0 수정: api.sajuos.com 절대주소 사용
const PROD_API_URL = 'https://api.sajuos.com';
const DEV_API_URL = 'http://localhost:8000';

const normalizeBaseUrl = (url: string) => url.replace(/\/+$/, ''); // trim trailing slashes
const normalizeEndpoint = (endpoint: string) => endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
const joinUrl = (base: string, endpoint: string) =>
  `${normalizeBaseUrl(base)}${normalizeEndpoint(endpoint)}`;

function getApiBaseUrl(): string {
  // ✅ 최우선: 배포/프리뷰/로컬 모두 환경변수로 제어
  const envUrl = process.env.NEXT_PUBLIC_API_URL;
  if (envUrl) return normalizeBaseUrl(envUrl);

  // ✅ 환경변수 없으면 NODE_ENV로 안전한 기본값 선택
  if (process.env.NODE_ENV === 'production') return normalizeBaseUrl(PROD_API_URL);
  return normalizeBaseUrl(DEV_API_URL);
}

export const API_BASE_URL = getApiBaseUrl();

// ============ 공통 Fetch ============

interface FetchOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  timeout?: number;
}

async function fetchApi<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const { method = 'GET', body, timeout = 30000 } = options;
  const fullUrl = joinUrl(API_BASE_URL, endpoint);
  
  // 🔥 P0 보안: 토큰이 포함될 수 있는 URL은 로깅하지 않음
  // 디버그가 필요한 경우에만 NEXT_PUBLIC_API_DEBUG=true 설정
  if (process.env.NEXT_PUBLIC_API_DEBUG === 'true') {
    // URL에서 토큰 마스킹 처리
    const safeUrl = fullUrl.replace(/([?&]token=)[^&]+/g, '$1***MASKED***');
    console.log(`[API] ${method} ${safeUrl}`);
  }
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(fullUrl, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = 
        errorData.message || 
        errorData.detail?.message || 
        errorData.detail ||
        `서버 오류 (${response.status})`;
      throw new Error(errorMessage);
    }
    
    return await response.json();
    
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('요청 시간이 초과되었습니다. 다시 시도해주세요.');
      }
      // 🔥 P0: 안전한 includes 처리
      const msg = typeof error.message === 'string' ? error.message : '';
      if (msg.includes('fetch') || msg.includes('Failed') || msg.includes('NetworkError')) {
        throw new Error('서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.');
      }
      throw error;
    }
    
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}

// ============ 기본 API 함수들 ============

export async function calculateSaju(
  data: CalculateRequest
): Promise<CalculateResponse> {
  return fetchApi<CalculateResponse>(
    '/api/v1/calculate',
    { method: 'POST', body: data, timeout: 15000 }
  );
}

export async function getHourOptions(): Promise<HourOption[]> {
  return fetchApi<HourOption[]>(
    '/api/v1/calculate/hour-options',
    { timeout: 10000 }
  );
}

export function getConcernTypes(): { concern_types: ConcernOption[] } {
  return {
    concern_types: [
      { value: 'love', label: '연애/결혼', emoji: '💕' },
      { value: 'wealth', label: '재물/금전', emoji: '💰' },
      { value: 'career', label: '직장/사업', emoji: '💼' },
      { value: 'health', label: '건강', emoji: '🏥' },
      { value: 'study', label: '학업/시험', emoji: '📚' },
      { value: 'general', label: '종합/기타', emoji: '🔮' },
    ]
  };
}

export async function healthCheck(): Promise<{ status: string }> {
  return fetchApi<{ status: string }>('/health', { timeout: 5000 });
}

export async function testConnection(): Promise<{
  success: boolean;
  apiUrl: string;
  error?: string;
}> {
  try {
    await healthCheck();
    return { success: true, apiUrl: API_BASE_URL };
  } catch (error) {
    return {
      success: false,
      apiUrl: API_BASE_URL,
      error: error instanceof Error ? error.message : '알 수 없는 오류'
    };
  }
}


// ============ 🔥 프리미엄 리포트 API ============

export interface ReportStartRequest {
  email: string;
  name?: string;
  saju_result?: CalculateResponse;
  year_pillar?: string;
  month_pillar?: string;
  day_pillar?: string;
  hour_pillar?: string;
  target_year?: number;
  question?: string;
  concern_type?: string;
  survey_data?: {
    industry?: string;
    business_stage?: string;
    monthly_revenue?: string;
    margin_percent?: number;
    cash_reserve?: string;
    primary_bottleneck?: string;
    goal_detail?: string;
    time_availability?: string;
    risk_tolerance?: string;
    urgent_question?: string;
  };
}

export interface ReportStartResponse {
  success: boolean;
  job_id: string;
  token: string;  // 🔥 P0: public_token
  status: string;
  message: string;
  view_url: string;  // 🔥 P0: 이메일 링크용
  status_url: string;
  result_url: string;
  poll_url: string;  // 레거시 호환
}

/**
 * 🔥 P0: 상태 머신 기반 응답 타입
 * - httpStatus로 분기 (에러가 아닌 상태 신호)
 */
export interface ReportStartResult {
  httpStatus: 200 | 403 | 503 | number;
  data?: ReportStartResponse;
  detail?: string;
}

export interface ReportViewResponse {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  result?: any;
  markdown?: string;
  error?: string;
}

/**
 * 🔥 프리미엄 리포트 생성 시작
 *
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 🔥 P0 상태 머신 규칙:
 * - 200: 성공 → 리포트 생성 진행
 * - 403: 결제 필요 → 결제 UX 진입 (에러 아님!)
 * - 503: 서버 점검 → 재시도 UX
 * - 기타: 오류 UX
 *
 * 📌 json()은 "성공일 때만" 호출
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export async function startReportGeneration(
  data: ReportStartRequest
): Promise<ReportStartResult> {
  const fullUrl = joinUrl(API_BASE_URL, '/api/v1/reports/start');

  try {
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    // 🔥 1️⃣ 결제 필요 (403/405) - 에러가 아닌 상태 신호
    // 403 Forbidden 또는 405 Method Not Allowed → PAYMENT_REQUIRED 상태
    if (response.status === 403 || response.status === 405) {
      const errorData = await response.json().catch(() => ({}));
      return {
        httpStatus: 403, // 내부적으로는 403으로 통일 (PAYMENT_REQUIRED 의미)
        detail: errorData.detail || '결제가 필요합니다',
      };
    }

    // 🔥 2️⃣ 서버 점검 (503) - 에러가 아닌 상태 신호
    if (response.status === 503) {
      const errorData = await response.json().catch(() => ({}));
      return {
        httpStatus: 503,
        detail: errorData.detail || '서버 점검 중입니다. 잠시 후 다시 시도해주세요.',
      };
    }

    // 🔥 3️⃣ 기타 오류
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        httpStatus: response.status,
        detail: errorData.detail || `서버 오류 (${response.status})`,
      };
    }

    // 🔥 4️⃣ 성공 (200) - json() 호출은 여기서만
    const responseData: ReportStartResponse = await response.json();
    return {
      httpStatus: 200,
      data: responseData,
    };

  } catch (error) {
    // 네트워크 오류
    return {
      httpStatus: 0,
      detail: error instanceof Error
        ? error.message
        : '네트워크 오류가 발생했습니다.',
    };
  }
}

/**
 * 🔥 P0 수정: job_id + token으로 리포트 조회
 * 엔드포인트: /api/v1/reports/view/{job_id}?token={token}
 */
export async function getReportByJobIdAndToken(
  jobId: string,
  token: string
): Promise<ReportViewResponse> {
  if (!jobId || !token) {
    throw new Error('job_id와 token이 필요합니다');
  }
  
  // 🔥 핵심: /view/{job_id}?token={token} 형식
  return fetchApi<ReportViewResponse>(
    `/api/v1/reports/view/${jobId}?token=${encodeURIComponent(token)}`,
    { timeout: 15000 }
  );
}

/**
 * 리포트 상태 조회 (폴링용, 토큰 옵션)
 */
export async function getReportStatus(
  jobId: string,
  token?: string
): Promise<ReportViewResponse> {
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  return fetchApi<ReportViewResponse>(
    `/api/v1/reports/${jobId}${tokenParam}`,
    { timeout: 10000 }
  );
}

/**
 * 리포트 결과 조회
 */
export async function getReportResult(
  jobId: string,
  token?: string
): Promise<ReportViewResponse> {
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  return fetchApi<ReportViewResponse>(
    `/api/v1/reports/${jobId}/result${tokenParam}`,
    { timeout: 10000 }
  );
}

/**
 * @deprecated getReportByJobIdAndToken 사용
 */
export async function getReportByToken(accessToken: string): Promise<ReportViewResponse> {
  // 레거시 호환: token만 있으면 job_id로 간주하고 에러
  console.warn('[API] getReportByToken is deprecated. Use getReportByJobIdAndToken instead.');
  throw new Error('job_id와 token이 모두 필요합니다. URL 형식: /report/{job_id}?token={token}');
}


// ============ 레거시 API ============

export async function interpretSaju(
  data: InterpretRequest
): Promise<InterpretResponse> {
  const result = await fetchApi<InterpretResponse>(
    '/api/v1/generate-report?mode=premium',
    { method: 'POST', body: data, timeout: 600000 }
  );
  
  if ((result as any).model_used === 'fallback') {
    throw new Error('AI 해석 서비스에 일시적인 문제가 발생했습니다.');
  }
  
  return result;
}

export async function regenerateSection(
  data: InterpretRequest,
  sectionId: string
): Promise<any> {
  return fetchApi<any>(
    `/api/v1/regenerate-section?section_id=${sectionId}`,
    { method: 'POST', body: data, timeout: 120000 }
  );
}
